import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Modal } from 'react-bootstrap';
import { Loading } from 'components/common/loading/Loading';
import {
    CloseButton,
    CloseButtonProps,
} from 'components/common/modal/CloseButton';
import { ServiceTrackingInfoProvider } from 'components/common/tracking/serviceTrackingContext';
import { useCustomParamsFromUrl } from 'components/hooks/UseCustomParams';
import { TopicTracked, ServiceTracked } from 'models/tracking';
import { ServiceLogo } from '../../common/logo/ServiceLogo';
import { ScreenshotCarousel } from './body/carousel/ScreenshotCarousel';
import { ServiceDescriptionFeatures } from './body/ServiceDescriptionFeatures';
import { ServiceDescriptionDocumentationPages } from './body/ServiceDescriptionDocumentationPages';
import { ServiceDescriptionMainText } from './body/ServiceDescriptionMainText';
import { ServiceDescriptionButtons } from './header/ServiceDescriptionButtons';
import { ServiceDescriptionTitle } from './header/ServiceDescriptionTitle';
import { useBoardServiceForDetail } from 'components/hooks/services/useBoardServices';
import { BookmarkOrigin, Service } from 'services/ApiService/ServiceBoard/ServiceBoardApiClient';

type ServiceDescriptionProps = {
    backLink: string;
};

export const ServiceDescription: FC<ServiceDescriptionProps> = ({ backLink }) => {
    const { pathname } = useLocation();
    const backPath = pathname.split('/service/')[0];
    const match = useCustomParamsFromUrl(`${backPath}/service/:serviceKey`);
    const navigate = useNavigate();
    const { loading, getSgmServiceByKey } = useBoardServiceForDetail();

    const sgmService = getSgmServiceByKey(match?.serviceKey ?? '');

    const closeModal = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        navigate(backLink);
    };

    return (
        <Modal
            show={true}
            centered={true}
            size="lg"
            backdrop="static"
            keyboard={false}
            aria-labelledby="serviceDescriptionTitle"
        >
            {loading ? (
                <LoadingModal closeModal={closeModal} />
            ) : sgmService ? (
                <DescriptionModal
                    sgmService={sgmService}
                    closeModal={closeModal}
                />
            ) : (
                <NotFoundModal closeModal={closeModal} />
            )}
        </Modal>
    );
};

const LoadingModal: FC<CloseButtonProps> = ({
    closeModal,
}) => (
    <>
        <Modal.Header>
            <Loading />
            <CloseButton closeModal={closeModal} />
        </Modal.Header>
        <Modal.Body />
    </>
);

const NotFoundModal: FC<CloseButtonProps> = ({
    closeModal,
}) => {
    const { t: translate } = useTranslation('service-description');
    return (
        <>
            <Modal.Header>
                <h1 className="modal-title h3" id="serviceDescriptionTitle">
                    {translate('service-description:notFoundTitle')}
                </h1>
                <CloseButton closeModal={closeModal} />
            </Modal.Header>
            <Modal.Body>
                {translate('service-description:notFoundBody')}
            </Modal.Body>
        </>
    );
};

type ServiceProps = {
    sgmService: Service;
};

type DescriptionProps = ServiceProps & CloseButtonProps;

const DescriptionModal: FC<DescriptionProps> = ({
    sgmService,
    closeModal,
}) => {
    const serviceTracked: ServiceTracked = {
        topic: TopicTracked.ServiceDetail,
        service: sgmService,
        origin: BookmarkOrigin.SgmService,
    };

    return (
        <ServiceTrackingInfoProvider serviceTracked={serviceTracked}>
            <Modal.Header>
                <DescriptionHeader
                    sgmService={sgmService}
                    closeModal={closeModal}
                />
            </Modal.Header>
            <Modal.Body>
                <DescriptionBody sgmService={sgmService} />
            </Modal.Body>
        </ServiceTrackingInfoProvider>
    );
};

const DescriptionHeader: FC<DescriptionProps> = ({
    sgmService,
    closeModal,
}) => {
    const { title, category, initials, isInternal, documentationPages } = sgmService;

    return (
        <div className="d-flex w-100">
            <div className="flex-0">
                <ServiceLogo initials={initials} size={120} />
            </div>
            <div className="flex-grow-1 ms-4 d-flex flex-column justify-content-between">
                <div className="flex-grow-1 d-flex justify-content-between align-items-start">
                    <ServiceDescriptionTitle name={title} categoryKey={category?.key} isInternal={isInternal} documentationPages={documentationPages} />
                    <CloseButton closeModal={closeModal} />
                </div>
                <div className="flex-grow-1 d-flex justify-content-between align-items-end">
                    <ServiceDescriptionButtons
                        sgmService={sgmService}
                    />
                </div>
            </div>
        </div>
    );
};

const DescriptionBody: FC<ServiceProps> = ({
    sgmService: { images, qumuId, features, descriptionText, documentationPages },
}) => {
    return (
        <div className="d-flex flex-column">
            <ScreenshotCarousel images={images} videoId={qumuId} />
            <ServiceDescriptionMainText text={descriptionText} />
            <ServiceDescriptionFeatures features={features} />
            <ServiceDescriptionDocumentationPages documentationPages={documentationPages} />
        </div>
    );
};
